const ZIP_LOCAL_FILE_HEADER = [0x50, 0x4b, 0x03, 0x04];
const ZIP_END_OF_CENTRAL_DIRECTORY = [0x50, 0x4b, 0x05, 0x06];
const ZIP_END_OF_CENTRAL_DIRECTORY_MIN_SIZE = 22;
const ZIP_MAX_COMMENT_SIZE = 0xffff;
const ZIP_TAIL_SIZE =
  ZIP_END_OF_CENTRAL_DIRECTORY_MIN_SIZE + ZIP_MAX_COMMENT_SIZE;

export const assertZipContentType = (
  contentType: string | null | undefined,
) => {
  const normalized = (contentType || '').split(';', 1)[0].trim().toLowerCase();
  if (normalized !== 'application/zip') {
    throw new Error(
      `服务器没有返回 ZIP（Content-Type: ${normalized || 'missing'}）`,
    );
  }
};

export const declaredZipResponseSize = (
  headers: Headers,
  expectedSize?: number,
) => {
  assertZipContentType(headers.get('Content-Type'));
  const rawLength = headers.get('Content-Length');
  const declaredSize = rawLength ? Number(rawLength) : Number.NaN;
  if (!Number.isSafeInteger(declaredSize) || declaredSize <= 0) {
    throw new Error('服务器没有返回有效的 ZIP 文件大小');
  }
  if (
    expectedSize !== undefined &&
    (!Number.isSafeInteger(expectedSize) ||
      expectedSize <= 0 ||
      declaredSize !== expectedSize)
  ) {
    throw new Error(
      `ZIP 文件大小不一致（应为 ${expectedSize} 字节，实际声明 ${declaredSize} 字节）`,
    );
  }
  return declaredSize;
};

export class ZipEnvelopeValidator {
  private prefix = new Uint8Array(ZIP_LOCAL_FILE_HEADER.length);

  private prefixLength = 0;

  private tail = new Uint8Array(0);

  private received = 0;

  push(chunk: Uint8Array) {
    if (!(chunk instanceof Uint8Array) || chunk.byteLength === 0) return;
    const prefixRemaining = this.prefix.length - this.prefixLength;
    if (prefixRemaining > 0) {
      const copyLength = Math.min(prefixRemaining, chunk.byteLength);
      this.prefix.set(chunk.subarray(0, copyLength), this.prefixLength);
      this.prefixLength += copyLength;
    }
    this.received += chunk.byteLength;
    if (chunk.byteLength >= ZIP_TAIL_SIZE) {
      this.tail = chunk.slice(chunk.byteLength - ZIP_TAIL_SIZE);
      return;
    }
    const keepFromExisting = Math.min(
      this.tail.byteLength,
      ZIP_TAIL_SIZE - chunk.byteLength,
    );
    const nextTail = new Uint8Array(keepFromExisting + chunk.byteLength);
    if (keepFromExisting > 0) {
      nextTail.set(
        this.tail.subarray(this.tail.byteLength - keepFromExisting),
        0,
      );
    }
    nextTail.set(chunk, keepFromExisting);
    this.tail = nextTail;
  }

  byteLength() {
    return this.received;
  }

  assertComplete(expectedSize?: number) {
    if (this.received <= 0) {
      throw new Error('服务器返回了 0 字节 ZIP');
    }
    if (expectedSize !== undefined && this.received !== expectedSize) {
      throw new Error(
        `ZIP 下载不完整（应为 ${expectedSize} 字节，实际收到 ${this.received} 字节）`,
      );
    }
    if (
      this.prefixLength !== ZIP_LOCAL_FILE_HEADER.length ||
      !ZIP_LOCAL_FILE_HEADER.every(
        (value, index) => this.prefix[index] === value,
      )
    ) {
      throw new Error('下载内容不是有效的 ZIP 文件');
    }
    for (
      let index = this.tail.byteLength - ZIP_END_OF_CENTRAL_DIRECTORY_MIN_SIZE;
      index >= 0;
      index -= 1
    ) {
      const hasEndRecord = ZIP_END_OF_CENTRAL_DIRECTORY.every(
        (value, offset) => this.tail[index + offset] === value,
      );
      if (hasEndRecord) {
        const commentLength =
          this.tail[index + 20] + this.tail[index + 21] * 256;
        if (
          index + ZIP_END_OF_CENTRAL_DIRECTORY_MIN_SIZE + commentLength ===
          this.tail.byteLength
        ) {
          return;
        }
      }
    }
    throw new Error('ZIP 下载被截断，缺少中央目录结束记录');
  }
}

export const assertCompleteZipBlob = async (
  blob: Blob,
  expectedSize?: number,
) => {
  if (!(blob instanceof Blob)) {
    throw new Error('服务器没有返回可下载文件');
  }
  const validator = new ZipEnvelopeValidator();
  const bytes = new Uint8Array(await blob.arrayBuffer());
  validator.push(bytes);
  validator.assertComplete(expectedSize);
};
